import { ContactSchema } from '@dreams-built/shared/src/schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { useContactMutation } from 'api/contact';
import { ReCaptchaProvider, useReCaptcha } from 'libs/Captcha';
import { useState, type FC } from 'react';
import { useForm } from 'react-hook-form';
import { type z } from 'zod';
import { trackEvent } from '../analytics';
import { COMPANY } from '../content';
import { Container, PhoneLink } from '../primitives';

type ContactInput = z.infer<typeof ContactSchema>;

const MAX_MESSAGE_LENGTH = 2000;

const inputClasses =
  'w-full rounded-md border border-ink-300 bg-white px-4 py-3 text-sm text-ink-900 placeholder:text-ink-400 focus:border-brand-600 focus:outline-2 focus:outline-offset-0 focus:outline-brand-500/30';

const Field: FC<{
  label: string;
  error?: string;
  htmlFor: string;
  children: React.ReactNode;
}> = ({ label, error, htmlFor, children }) => (
  <div>
    <label htmlFor={htmlFor} className="mb-1.5 block text-sm font-medium text-ink-800">
      {label}
    </label>
    {children}
    {error ? (
      <p role="alert" className="mt-1.5 text-xs text-red-600">
        {error}
      </p>
    ) : null}
  </div>
);

const ContactForm: FC = () => {
  const mutation = useContactMutation();
  const { execute, reset: resetCaptcha } = useReCaptcha();
  const [status, setStatus] = useState<'idle' | 'sent' | 'error'>('idle');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<ContactInput>({
    resolver: zodResolver(ContactSchema),
    defaultValues: { name: '', email: '', phoneNumber: '', message: '' }
  });

  const messageLength = (watch('message') || '').length;

  const onSubmit = handleSubmit(async (input) => {
    setStatus('idle');
    const token = execute();
    if (!token) {
      setStatus('error');
      return;
    }
    try {
      await mutation.mutateAsync(input);
      reset();
      resetCaptcha();
      setStatus('sent');
      trackEvent('contact_form_submit');
    } catch {
      setStatus('error');
    }
  });

  if (status === 'sent') {
    return (
      <div className="rounded-lg border border-ink-200 bg-white p-8 text-center">
        <p className="font-mono text-xs tracking-[0.2em] text-brand-600 uppercase">Received</p>
        <h3 className="font-display mt-3 text-2xl font-bold text-ink-900">
          Thanks, we&rsquo;re onto it.
        </h3>
        <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-ink-600">
          Your message is with the team. Expect to hear back within one working day, sooner for
          quotes with plans attached.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 text-sm font-semibold text-brand-600 hover:text-brand-700"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Name" htmlFor="contact-name" error={errors.name?.message}>
          <input
            id="contact-name"
            autoComplete="name"
            className={inputClasses}
            placeholder="Your name"
            {...register('name')}
          />
        </Field>
        <Field label="Phone (optional)" htmlFor="contact-phone" error={errors.phoneNumber?.message}>
          <input
            id="contact-phone"
            type="tel"
            autoComplete="tel"
            className={inputClasses}
            placeholder="021 …"
            {...register('phoneNumber')}
          />
        </Field>
      </div>
      <Field label="Email" htmlFor="contact-email" error={errors.email?.message}>
        <input
          id="contact-email"
          type="email"
          autoComplete="email"
          className={inputClasses}
          placeholder="you@example.co.nz"
          {...register('email')}
        />
      </Field>
      <Field label="What can we help with?" htmlFor="contact-message" error={errors.message?.message}>
        <textarea
          id="contact-message"
          rows={6}
          maxLength={MAX_MESSAGE_LENGTH}
          className={`${inputClasses} resize-y`}
          placeholder="Tell us about the job: address, rough size, timeframe. If you have plans, mention it and we'll reply with an email to send them to."
          {...register('message')}
        />
        <p className="mt-1.5 text-right font-mono text-[11px] text-ink-400">
          {messageLength}/{MAX_MESSAGE_LENGTH}
        </p>
      </Field>

      {status === 'error' ? (
        <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Something went wrong sending your message. Please try again, or call us on{' '}
          <PhoneLink className="font-semibold underline" /> instead.
        </p>
      ) : null}

      <button
        type="submit"
        disabled={isSubmitting || mutation.isPending}
        className="w-full rounded-md bg-brand-600 px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
      >
        {isSubmitting || mutation.isPending ? 'Sending…' : 'Send message'}
      </button>
    </form>
  );
};

export const ContactPanel: FC<{ sheet?: string }> = ({ sheet = 'DB-05 - Get a quote' }) => (
  <section className="py-20 sm:py-28">
    <Container>
      <div className="grid gap-12 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <p className="font-mono text-[11px] tracking-[0.2em] text-ink-500 uppercase">{sheet}</p>
          <div className="mt-2 mb-5 h-px w-full bg-ink-200" />
          <h2 className="font-display text-3xl leading-tight font-bold text-ink-900 sm:text-4xl">
            Tell us about the job
          </h2>
          <p className="mt-4 text-base leading-relaxed text-ink-600">
            Plans, a sketch, or just an address and an idea. Send it through and we&rsquo;ll come
            back with a fixed quote, usually within two working days.
          </p>

          <dl className="mt-10 space-y-6">
            <div>
              <dt className="font-mono text-[11px] tracking-[0.2em] text-ink-500 uppercase">Phone</dt>
              <dd className="mt-1">
                <PhoneLink className="font-mono text-lg font-medium text-ink-900 hover:text-brand-600">
                  {COMPANY.phone}
                </PhoneLink>
              </dd>
            </div>
            <div>
              <dt className="font-mono text-[11px] tracking-[0.2em] text-ink-500 uppercase">Email</dt>
              <dd className="mt-1">
                <a
                  href={COMPANY.emailHref}
                  className="text-lg font-medium text-ink-900 hover:text-brand-600"
                >
                  {COMPANY.email}
                </a>
              </dd>
            </div>
            <div>
              <dt className="font-mono text-[11px] tracking-[0.2em] text-ink-500 uppercase">Hours</dt>
              <dd className="mt-1 text-ink-700">{COMPANY.hours}</dd>
            </div>
            <div>
              <dt className="font-mono text-[11px] tracking-[0.2em] text-ink-500 uppercase">
                Service area
              </dt>
              <dd className="mt-1 text-ink-700">
                {COMPANY.serviceAreas.slice(0, -1).join(', ')} and the {COMPANY.serviceAreas.at(-1)}
              </dd>
            </div>
          </dl>
        </div>

        <div className="rounded-lg border border-ink-200 bg-white p-6 sm:p-8">
          <ReCaptchaProvider>
            <ContactForm />
          </ReCaptchaProvider>
        </div>
      </div>
    </Container>
  </section>
);
